import type { RoomLayoutInput } from '../schemas/rooms';

/**
 * Room layouts (docs/tech/content-schemas.md §3.6, docs/design/content-items.md §F).
 * Grid: (0,0) is the back corner; the left wall runs along x = 0, the right wall along y = 0.
 */
export const HOME_LAYOUT = {
  id: 'home',
  sceneId: 'home',
  size: { w: 8, h: 8 },
  floor: [{ x: 0, y: 0, w: 8, h: 8 }],
  walls: { left: true, right: true },
  openings: [
    { kind: 'window', wall: 'left', offset: 3, span: 2 }, // left-wall window, cells y = 3..4
    { kind: 'door', wall: 'right', offset: 2, span: 1 }, // right-wall door, cell x = 2
  ],
  zones: [
    { id: 'window', rects: [{ x: 0, y: 2, w: 2, h: 4 }], blocksObjects: false }, // 2 rows by the window
    { id: 'kitchen', rects: [{ x: 5, y: 0, w: 3, h: 2 }], blocksObjects: false }, // back-right 3×2 corner
    {
      id: 'corner',
      rects: [
        { x: 0, y: 0, w: 2, h: 2 },
        { x: 6, y: 0, w: 2, h: 2 },
        { x: 0, y: 6, w: 2, h: 2 },
        { x: 6, y: 6, w: 2, h: 2 },
      ],
      blocksObjects: false,
    },
    { id: 'center', rects: [{ x: 2, y: 2, w: 4, h: 4 }], blocksObjects: false },
    { id: 'door', rects: [{ x: 2, y: 0, w: 1, h: 1 }], blocksObjects: true }, // keep the entrance clear
  ],
  fixtures: [],
  expansions: [
    {
      id: 'exp_home_sleep_nook',
      unlockId: 'unl_home_layout_2',
      addRects: [{ x: 8, y: 4, w: 4, h: 4 }],
      zones: [{ id: 'sleep_nook', rects: [{ x: 8, y: 4, w: 4, h: 4 }], blocksObjects: false }],
    },
  ],
  boxSpawns: [
    { boxDefId: 'box_01_sleep', x: 3, y: 3 },
    { boxDefId: 'box_02_work', x: 4, y: 3 },
    { boxDefId: 'box_03_books', x: 3, y: 4 },
    { boxDefId: 'box_04_kitchen', x: 4, y: 4 },
    { boxDefId: 'box_05_granny', x: 5, y: 5 },
    { boxDefId: 'box_06_shop', x: 5, y: 4 },
  ],
  catSpots: [{ x: 1, y: 3 }],
  interactiveObjects: [
    {
      id: 'dusty_chest',
      sprite: 'obj_dusty_chest',
      anchor: { side: 'frontLeft', offset: 1 }, // front-left, by the window side (ux-screens §3.1)
      // After the FTUE dusting (box 5, T06), so the tutorial run stays the first one.
      requires: [{ type: 'miniGameCleared', miniGameId: 'mg_dusting', level: 1 }],
    },
    {
      id: 'mailbox',
      sprite: 'obj_mailbox',
      anchor: { side: 'frontRight', offset: 2 }, // front-right, clear of the sleep nook expansion (y 4..7)
      requires: [{ type: 'unlocked', unlockId: 'unl_minimart' }],
    },
  ],
  camera: { interest: { x: 0, y: 0, w: 8, h: 8 }, minZoom: 0.5, maxZoom: 2 },
  defaultWall: 'wall_cream',
  defaultFloor: 'floor_wood',
} as const satisfies RoomLayoutInput;

export const MINIMART_LAYOUT = {
  id: 'minimart',
  sceneId: 'minimart',
  size: { w: 8, h: 10 },
  floor: [{ x: 0, y: 0, w: 8, h: 10 }],
  walls: { left: true, right: true },
  openings: [{ kind: 'window', wall: 'right', offset: 5, span: 2 }],
  zones: [
    // Front door on the front edge (x = 7); the main aisle runs from the door to the counter.
    { id: 'aisle', rects: [{ x: 3, y: 4, w: 5, h: 2 }], blocksObjects: true },
    {
      id: 'corner',
      rects: [
        { x: 0, y: 0, w: 2, h: 2 },
        { x: 6, y: 0, w: 2, h: 2 },
        { x: 0, y: 8, w: 2, h: 2 },
      ],
      blocksObjects: false,
    },
    { id: 'window', rects: [{ x: 4, y: 0, w: 4, h: 2 }], blocksObjects: false },
    { id: 'center', rects: [{ x: 2, y: 3, w: 4, h: 4 }], blocksObjects: false },
  ],
  fixtures: [
    { itemId: 'fix_checkout_counter', x: 1, y: 4, rotation: 0, movable: false },
    { itemId: 'fix_shelf_small', x: 0, y: 1, rotation: 90, movable: true },
    { itemId: 'fix_shelf_small', x: 0, y: 7, rotation: 90, movable: true },
    { itemId: 'fix_basket_display', x: 3, y: 7, rotation: 0, movable: true },
  ],
  expansions: [
    {
      id: 'exp_minimart_1',
      unlockId: 'unl_shop_expansion_1',
      addRects: [{ x: 8, y: 0, w: 2, h: 10 }], // +2×10 → 10×10 room, still rectangular
    },
  ],
  customerPath: { entry: { x: 7, y: 5 }, checkout: { x: 2, y: 5 } },
  catSpots: [
    { x: 2, y: 2 },
    { x: 5, y: 8 },
  ],
  interactiveObjects: [
    {
      id: 'packing_crate',
      sprite: 'obj_packing_pile',
      anchor: { side: 'frontLeft', offset: 6 }, // front-left corner, clear of the +2 columns expansion
      requires: [{ type: 'unlocked', unlockId: 'unl_packing' }],
    },
    {
      id: 'order_board',
      sprite: 'obj_shop_order_board',
      anchor: { side: 'left', offset: 5 }, // the free wall between the starter shelves (y = 1 and 7)
      requires: [{ type: 'unlocked', unlockId: 'unl_npc_orders' }],
    },
  ],
  camera: { interest: { x: 0, y: 0, w: 8, h: 10 }, minZoom: 0.5, maxZoom: 2 },
  defaultWall: 'wall_cream',
  defaultFloor: 'floor_wood',
} as const satisfies RoomLayoutInput;

/**
 * Studio NANO (docs/design/studio-nano.md §3): a small 6×6 set. The client stands on the taped pose mark in
 * the middle; studio props belong on the stage around it. Tiếng lành Lv3 adds two rows towards the front.
 */
export const STUDIO_LAYOUT = {
  id: 'studio',
  sceneId: 'studio',
  size: { w: 6, h: 6 },
  floor: [{ x: 0, y: 0, w: 6, h: 6 }],
  walls: { left: true, right: true },
  openings: [
    { kind: 'window', wall: 'left', offset: 3, span: 2 }, // left-wall window, cells y = 3..4
    { kind: 'door', wall: 'right', offset: 4, span: 1 }, // right-wall door, cell x = 4
  ],
  zones: [
    { id: 'pose', rects: [{ x: 2, y: 2, w: 1, h: 1 }], blocksObjects: true }, // the client stands here
    { id: 'stage', rects: [{ x: 1, y: 1, w: 3, h: 3 }], blocksObjects: false },
    { id: 'door', rects: [{ x: 4, y: 0, w: 1, h: 1 }], blocksObjects: true },
  ],
  fixtures: [],
  expansions: [
    {
      id: 'exp_studio_1',
      unlockId: 'unl_studio_lv3',
      addRects: [{ x: 6, y: 0, w: 2, h: 6 }], // +2×6 → 8×6, still rectangular
    },
  ],
  catSpots: [{ x: 4, y: 4 }],
  interactiveObjects: [
    {
      id: 'booking_board',
      sprite: 'obj_studio_booking_board',
      anchor: { side: 'left', offset: 1 }, // the wall between the back corner and the window (y = 3..4)
      requires: [{ type: 'unlocked', unlockId: 'unl_studio' }],
    },
  ],
  camera: { interest: { x: 0, y: 0, w: 6, h: 6 }, minZoom: 0.5, maxZoom: 2 },
  defaultWall: 'wall_studio_paper',
  defaultFloor: 'floor_studio_parquet',
} as const satisfies RoomLayoutInput;
